import { Box } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import { useGetUnmatchedClaimstoinvoices } from '@/hooks/service-hooks/billingTools';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getUnmatchedClaimsToInvoices } from '@/services/billingTools';
import type {
	UnmatchedClaimsToInvoices,
} from '@/types/billingTools';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { unmatchedClaimsToInvoicesColumns } from '../billingTools';

const CLINIC_KEY = 'clinicName';

/**
 * UnmatchedClaimstoinvoicesTable Component
 *
 * Displays claims that exist in the EMR system but do not have corresponding invoices.
 * Helps identify billing gaps where claims were filed but invoices were not generated.
 *
 * Features:
 * - Real-time display of unmatched claims
 * - Shows patient info, clinic, insurance, charges, payments, and units
 * - Filterable by clinic
 * - Export functionality (CSV, Excel)
 */
const UnmatchedClaimstoinvoicesTable = () => {
	// Shared clinic filter helpers (key used by Unmatched Claims API)
	const { defaultClinicNames, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	// Table filter state: manages pagination, search, sorting, and tab selection
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
						{
							key: CLINIC_KEY,
							values: defaultClinicNames,
						},
					]
				: [],
		),
		search: '',
		enabled: defaultClinicNames.length > 0,
		tab: '',
		sort: '',
	});

	const columns = useMemo(() => unmatchedClaimsToInvoicesColumns, []);

	// Fetch unmatched claims data based on current filters
	const { data, isFetching } = useGetUnmatchedClaimstoinvoices({
		...filters,
		enabled: JSON.parse(filters.globalFilters || '[]').length > 0,
	});

	/**
	 * Transform unmatched claims data for table display
	 * Converts raw API data into table-ready format with:
	 * - Formatted post dates
	 * - Patient and clinic information
	 * - Insurance details
	 * - Charges, payments, and units totals
	 */
	const transformedData = useMemo(() => {
		if (!data?.items) return [];
		const unmatchedClaimsToInvoicesData = data.items;

		return unmatchedClaimsToInvoicesData.map((item) => {
			return {
				id: item.patientid,
				patientName: item.patientName ? (
					<FaceliftTruncatedTextWithTooltip text={item.patientName} maxLength={20} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={20} />
				),
				patientid: item.patientid || '--',
				srvday: item.srvday || '--',
				proccode: item.proccode || '--',
				primaryClmInsPkgName: item.primaryClmInsPkgName ? (
					<FaceliftTruncatedTextWithTooltip text={item.primaryClmInsPkgName} maxLength={35} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={35} />
				),
				clinicName: item.clinicName ? (
					<FaceliftTruncatedTextWithTooltip text={item.clinicName} maxLength={40} />
				) : (
					<FaceliftTruncatedTextWithTooltip text="--" maxLength={40} />
				),
				total_charges: item.total_charges ?? '--',
				total_pmts: item.total_pmts ?? '--',
				total_units: item.total_units ?? '--',
				postdate: item.postdate
					? formatDateToString(new Date(item.postdate))
					: '--',
			};
		});
	}, [data]);

	/**
	 * Transform unmatched claims data for export
	 * Converts data to plain text format for CSV/Excel compatibility
	 */
	const transformedDataToExport = (
		unmatchedClaimstoinvoicesData: UnmatchedClaimsToInvoices[],
	) => {
		if (!unmatchedClaimstoinvoicesData.length) return [];

		return unmatchedClaimstoinvoicesData.map((item) => {
			return {
				id: item.patientid,
				patientName: item.patientName || '--',
				patientid: item.patientid || '--',
				srvday: item.srvday || '--',
				proccode: item.proccode || '--',
				primaryClmInsPkgName: item.primaryClmInsPkgName || '--',
				clinicName: item.clinicName || '--',
				total_charges: item.total_charges ?? '--',
				total_pmts: item.total_pmts ?? '--',
				total_units: item.total_units ?? '--',
				postdate: item.postdate
					? formatDateToString(new Date(item.postdate))
					: '--',
			};
		});
	};

	/**
	 * Handle filter changes from DataTable
	 * Updates filter state to trigger new data fetch with applied filters
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				globalFilters: JSON.stringify(mappedFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters],
	);

	/**
	 * Fetch all unmatched claims records for export
	 * Iterates through all pages to get complete dataset with current filters applied
	 * @returns Complete unmatched claims data formatted for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: UnmatchedClaimsToInvoices[] = [];
		let page = 1;
		let hasMore = true;

		while (hasMore) {
			const response = await getUnmatchedClaimsToInvoices(
				page,
				9999,
				filters.search,
				(filters.status as string) || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const unmatchedClaimstoinvoicesDataForExcel = { items: allItems };
		const transformedData = transformedDataToExport(
			unmatchedClaimstoinvoicesDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export unmatched claims data to CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`UnmatchedClaimsToInvoices-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export unmatched claims data to Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`UnmatchedClaimsToInvoices-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			<DataTable
				tableName="Unmatched Claims To Invoices"
				loading={isFetching}
				items={transformedData}
				columns={columns}
				topLabel="Unmatched Claims To Invoices"
				totalCount={data?.meta.totalItems || 0}
				onFilterApply={onFilterApply}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				globalFilter={{
					title: 'Filters',
					items: [
						{
							options: defaultClinicNames.map((clinic) => ({
								label: clinic,
								id: clinic,
							})),
							key: 'Clinics',
							name: 'Clinics',
						},
					],
				}}
				actions={[]}
				exportable
				hideAllStatusFilter={true}
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>
		</Box>
	);
};

export default UnmatchedClaimstoinvoicesTable;
